import type { Node as ProseMirrorNode } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection, type Selection } from '@atlaskit/editor-prosemirror/state';
import { getParameter } from '@atlaskit/native-embeds-common/utils';

import { getExperienceManifestRegistry } from '@atlassian/native-embeds-core/services/experience-manifest-registry/main';
import type { ExperienceManifest } from '@atlassian/native-embeds-core/types';

import { expVal } from '@confluence/feature-experiments';

export type ExtensionCommentsManifest = Pick<ExperienceManifest, 'comments'>;

export type ExtensionCommentsEligibility = {
	manifest?: ExtensionCommentsManifest | null;
	node?: ProseMirrorNode | null;
};

export type CommentToolbarIconEligibility = {
	canAddComments: boolean;
	isGeneratingOrReplacing: boolean;
	isOffline: boolean;
	isReadOnly: boolean;
	manifest?: ExtensionCommentsManifest | null;
	selection: Selection;
};

/**
 * Product rollout policy for extension comments. This intentionally does not
 * inspect or mutate annotation marks, so disabling the experience only hides
 * extension comment surfaces and leaves stored comments intact.
 */
export const isExtensionCommentsExperienceEnabled = ({
	manifest,
	node,
}: ExtensionCommentsEligibility): boolean => {
	if (node?.type.name !== 'extension') {
		return false;
	}

	try {
		if (manifest?.comments?.enabled !== true) {
			return false;
		}
		return expVal('cc_maui_comments_on_charts_exp', 'isEnabled', false);
	} catch {
		return false;
	}
};

/**
 * Creation-surface policy for extension comments. Existing comment viewing is
 * deliberately handled separately with view permissions.
 */
export const canShowCommentToolbarIcon = ({
	canAddComments,
	isGeneratingOrReplacing,
	isOffline,
	isReadOnly,
	manifest,
	selection,
}: CommentToolbarIconEligibility): boolean => {
	if (
		!canAddComments ||
		isGeneratingOrReplacing ||
		isOffline ||
		isReadOnly ||
		!(selection instanceof NodeSelection)
	) {
		return false;
	}

	return isExtensionCommentsExperienceEnabled({ manifest, node: selection.node });
};

/**
 * Native embed nodes also carry the registered experience id in extensionKey.
 * Keep this as a fallback for nodes whose URL was rewritten or is not present
 * in the persisted ADF.
 */
const getExtensionExperienceId = (node: ProseMirrorNode): string | undefined => {
	const extensionKey = node.attrs?.extensionKey;

	if (typeof extensionKey !== 'string') {
		return undefined;
	}

	const prefix = 'native-embed:';
	return extensionKey.startsWith(prefix) ? extensionKey.slice(prefix.length) : undefined;
};

/**
 * Resolves the registered experience manifest that owns an `extension` node.
 *
 * Only the `comments` slice of the manifest is needed here — the eligibility
 * helpers deliberately do not reach for anything else, so an experience opts
 * into extension comments purely by declaring `comments.enabled`.
 */
export const getExtensionCommentsManifest = (
	node?: ProseMirrorNode | null,
): ExtensionCommentsManifest | undefined => {
	if (!node || node.type.name !== 'extension') {
		return undefined;
	}

	try {
		const registry = getExperienceManifestRegistry();
		const url = getParameter(node.attrs.parameters, 'url');
		const manifestByUrl = url ? registry.getManifestByUrl(url) : undefined;
		const experienceId = getExtensionExperienceId(node);
		const manifestById = experienceId ? registry.getManifestById(experienceId) : undefined;
		const manifest = manifestByUrl ?? manifestById;

		return manifest;
	} catch {
		return undefined;
	}
};

/**
 * Builds the node-aware annotation predicate consumed by
 * `InlineCommentAnnotationProvider.isBlockNodeSupported`.
 */
export const isExtensionCommentBlockNodeSupported = (node: ProseMirrorNode): boolean =>
	isExtensionCommentsExperienceEnabled({ manifest: getExtensionCommentsManifest(node), node });
